import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, Plus, Trash2, Bold, Italic, Underline, List, ListOrdered, Link2 } from 'lucide-react';

const EMPTY_FORM = {
  name: '',
  subject_template: '',
  body_html_template: '',
  dynamic_fields: []
};

export default function EmailTemplatesPage() {
  const { hasPermission } = useAuth();
  const canManageSettings = hasPermission('all') || hasPermission('settings');

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    key: '',
    name: '',
    subject_template: '',
    body_html_template: '',
    dynamic_fields: ''
  });
  const [newFieldName, setNewFieldName] = useState('');
  const [samplePayloadText, setSamplePayloadText] = useState('{}');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);

  const bodyEditorRef = useRef(null);
  const subjectInputRef = useRef(null);

  const buildSamplePayloadText = (fields = []) => {
    const payload = {};
    (fields || []).forEach((field) => {
      payload[field] = `sample_${field}`;
    });
    return JSON.stringify(payload, null, 2);
  };

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.key === selectedKey),
    [templates, selectedKey]
  );

  const resetCreateForm = () => {
    setCreateForm({
      key: '',
      name: '',
      subject_template: '',
      body_html_template: '',
      dynamic_fields: ''
    });
  };

  const loadTemplates = async (preferredKey = '') => {
    setLoading(true);
    try {
      const response = await adminApi.getEmailTemplates();
      const items = response.data?.items || [];
      setTemplates(items);

      const activeKey = (preferredKey && items.some((item) => item.key === preferredKey))
        ? preferredKey
        : (selectedKey && items.some((item) => item.key === selectedKey))
          ? selectedKey
          : (items[0]?.key || '');

      const target = items.find((item) => item.key === activeKey);
      setSelectedKey(activeKey);
      setForm(target ? {
        name: target.name || '',
        subject_template: target.subject_template || '',
        body_html_template: target.body_html_template || '',
        dynamic_fields: target.dynamic_fields || []
      } : EMPTY_FORM);
      setSamplePayloadText(buildSamplePayloadText(target?.dynamic_fields || []));
      setPreviewResult(null);
      if (bodyEditorRef.current) {
        bodyEditorRef.current.innerHTML = target?.body_html_template || '';
      }
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (!bodyEditorRef.current) return;
    if (document.activeElement === bodyEditorRef.current) return;
    if (bodyEditorRef.current.innerHTML !== (form.body_html_template || '')) {
      bodyEditorRef.current.innerHTML = form.body_html_template || '';
    }
  }, [form.body_html_template, selectedKey]);

  const handleSelectTemplate = (key) => {
    const template = templates.find((item) => item.key === key);
    setSelectedKey(key);
    setForm(template ? {
      name: template.name || '',
      subject_template: template.subject_template || '',
      body_html_template: template.body_html_template || '',
      dynamic_fields: template.dynamic_fields || []
    } : EMPTY_FORM);
    setSamplePayloadText(buildSamplePayloadText(template?.dynamic_fields || []));
    setPreviewResult(null);
    if (bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = template?.body_html_template || '';
    }
  };

  const handlePreview = async () => {
    if (!selectedKey) {
      toast.error('Please select a template');
      return;
    }
    let samplePayload = {};
    try {
      samplePayload = samplePayloadText.trim() ? JSON.parse(samplePayloadText) : {};
    } catch (error) {
      toast.error('Sample payload must be valid JSON');
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await adminApi.previewEmailTemplate(selectedKey, {
        sample_payload: samplePayload,
      });
      setPreviewResult(response.data || null);
      toast.success('Preview generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyEditorCommand = (command, value) => {
    if (!bodyEditorRef.current) return;
    bodyEditorRef.current.focus();
    document.execCommand(command, false, value);
    setForm((prev) => ({ ...prev, body_html_template: bodyEditorRef.current.innerHTML || '' }));
  };

  const addDynamicField = () => {
    const value = newFieldName.trim();
    if (!value) return;
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value)) {
      toast.error('Dynamic field must match: letters/numbers/underscore (start with letter/underscore)');
      return;
    }
    setForm((prev) => ({
      ...prev,
      dynamic_fields: prev.dynamic_fields.includes(value)
        ? prev.dynamic_fields
        : [...prev.dynamic_fields, value]
    }));
    setNewFieldName('');
  };

  const removeDynamicField = (value) => {
    setForm((prev) => ({
      ...prev,
      dynamic_fields: prev.dynamic_fields.filter((item) => item !== value)
    }));
  };

  const insertTokenInSubject = (token) => {
    const placeholder = `{{${token}}}`;
    const input = subjectInputRef.current;
    if (!input) {
      setForm((prev) => ({ ...prev, subject_template: `${prev.subject_template}${placeholder}` }));
      return;
    }
    const start = input.selectionStart ?? form.subject_template.length;
    const end = input.selectionEnd ?? start;
    const next = `${form.subject_template.slice(0, start)}${placeholder}${form.subject_template.slice(end)}`;
    setForm((prev) => ({ ...prev, subject_template: next }));
    requestAnimationFrame(() => {
      input.focus();
      const cursor = start + placeholder.length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  const insertTokenInBody = (token) => {
    const placeholder = `{{${token}}}`;
    if (bodyEditorRef.current) {
      bodyEditorRef.current.focus();
      document.execCommand('insertText', false, placeholder);
      setForm((prev) => ({ ...prev, body_html_template: bodyEditorRef.current.innerHTML || '' }));
      return;
    }
    setForm((prev) => ({ ...prev, body_html_template: `${prev.body_html_template}${placeholder}` }));
  };

  const handleInsertLink = () => {
    const url = window.prompt('Enter URL (including https://)');
    if (!url) return;
    applyEditorCommand('createLink', url);
  };

  const handleSave = async () => {
    if (!selectedKey) {
      toast.error('Please select a template');
      return;
    }
    if (!form.subject_template.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!form.body_html_template.trim()) {
      toast.error('Body content is required');
      return;
    }

    setSaving(true);
    try {
      await adminApi.updateEmailTemplate(selectedKey, {
        name: form.name,
        subject_template: form.subject_template,
        body_html_template: form.body_html_template,
        dynamic_fields: form.dynamic_fields
      });
      toast.success('Template updated');
      await loadTemplates(selectedKey);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    const key = createForm.key.trim().toLowerCase();
    const name = createForm.name.trim();
    const subject = createForm.subject_template.trim();
    const body = createForm.body_html_template.trim();
    if (!key || !name || !subject || !body) {
      toast.error('Key, Name, Subject and Body are required');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(key)) {
      toast.error('Template key supports only lowercase letters, numbers, and underscore');
      return;
    }

    const dynamicFields = createForm.dynamic_fields
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      await adminApi.createEmailTemplate({
        key,
        name,
        subject_template: subject,
        body_html_template: body,
        dynamic_fields: dynamicFields
      });
      toast.success('Template created');
      setShowCreateDialog(false);
      resetCreateForm();
      await loadTemplates(key);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate || selectedTemplate.is_system) return;
    const ok = window.confirm(`Delete template "${selectedTemplate.name}"?`);
    if (!ok) return;
    setDeleting(true);
    try {
      await adminApi.deleteEmailTemplate(selectedTemplate.key);
      toast.success('Template deleted');
      await loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  if (!canManageSettings) {
    return (
      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-[#2D2420]">Email Templates</CardTitle>
          <CardDescription>You do not have permission to manage email templates.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Email Templates</h1>
          <p className="text-[#A8A8A8] mt-1">Create and manage email subjects, content and dynamic fields.</p>
        </div>
        <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="pt-6 space-y-4">
          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">Template</Label>
                  <Select value={selectedKey} onValueChange={handleSelectTemplate}>
                    <SelectTrigger className="border-[#E5E5E5] focus:border-[#C97A5E]">
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.key} value={template.key}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">Template Key</Label>
                  <Input value={selectedTemplate?.key || ''} disabled className="border-[#E5E5E5]" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#5C4033]">Template Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  disabled={selectedTemplate?.is_system}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#5C4033]">Subject</Label>
                <Input
                  ref={subjectInputRef}
                  value={form.subject_template}
                  onChange={(e) => setForm((prev) => ({ ...prev, subject_template: e.target.value }))}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#5C4033]">Body (WYSIWYG + HTML)</Label>
                <div className="flex flex-wrap gap-2 p-2 border border-[#E5E5E5] rounded-t-md bg-[#FAFAF9]">
                  <Button type="button" variant="outline" size="sm" onClick={() => applyEditorCommand('bold')}>
                    <Bold className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => applyEditorCommand('italic')}>
                    <Italic className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => applyEditorCommand('underline')}>
                    <Underline className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => applyEditorCommand('insertUnorderedList')}>
                    <List className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => applyEditorCommand('insertOrderedList')}>
                    <ListOrdered className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={handleInsertLink}>
                    <Link2 className="w-4 h-4" />
                  </Button>
                </div>
                <div
                  ref={bodyEditorRef}
                  contentEditable
                  suppressContentEditableWarning
                  className="min-h-[280px] border border-t-0 border-[#E5E5E5] rounded-b-md p-3 focus:outline-none focus:ring-2 focus:ring-[#C97A5E]/30 text-sm"
                  onInput={(e) => setForm((prev) => ({ ...prev, body_html_template: e.currentTarget.innerHTML }))}
                />
                <Textarea
                  value={form.body_html_template}
                  onChange={(e) => setForm((prev) => ({ ...prev, body_html_template: e.target.value }))}
                  className="min-h-[140px] border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-[#5C4033]">Dynamic Fields</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    placeholder="e.g. customer_name"
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                  <Button type="button" variant="outline" onClick={addDynamicField}>Add Field</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(form.dynamic_fields || []).map((field) => (
                    <div key={field} className="flex items-center gap-2">
                      <Badge className="bg-[#F2ECE8] text-[#5C4033]">{`{{${field}}}`}</Badge>
                      <Button type="button" size="sm" variant="outline" onClick={() => insertTokenInSubject(field)}>Subject</Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => insertTokenInBody(field)}>Body</Button>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeDynamicField(field)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3 border border-[#E5E5E5] rounded-xl p-4 bg-[#FAFAF9]">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[#5C4033]">Template Preview Test (Sample Payload JSON)</Label>
                  <Button type="button" variant="outline" onClick={handlePreview} disabled={previewLoading || !selectedKey}>
                    {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Preview Rendered Template
                  </Button>
                </div>
                <Textarea
                  value={samplePayloadText}
                  onChange={(e) => setSamplePayloadText(e.target.value)}
                  className="min-h-[120px] border-[#E5E5E5] bg-white"
                />

                {previewResult && (
                  <div className="space-y-3 border border-[#E5E5E5] rounded-lg p-3 bg-white">
                    <div>
                      <p className="text-xs text-[#A8A8A8]">Subject Preview</p>
                      <p className="font-medium text-[#2D2420]">{previewResult.subject_preview || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#A8A8A8] mb-1">Body Preview</p>
                      <div className="border border-[#E5E5E5] rounded-md p-3 max-h-72 overflow-auto" dangerouslySetInnerHTML={{ __html: previewResult.body_html_preview || '' }} />
                    </div>
                    {!!(previewResult.unresolved_fields || []).length && (
                      <div>
                        <p className="text-xs text-[#A8A8A8] mb-1">Unresolved Fields</p>
                        <div className="flex flex-wrap gap-2">
                          {(previewResult.unresolved_fields || []).map((field) => (
                            <Badge key={field} className="bg-red-100 text-red-700">{`{{${field}}}`}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={!selectedTemplate || selectedTemplate.is_system || deleting}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete Template
                </Button>
                <Button onClick={handleSave} disabled={saving || !selectedKey} className="bg-[#C97A5E] hover:bg-[#B06850]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Template
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetCreateForm(); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Email Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Key</Label>
                <Input
                  value={createForm.key}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, key: e.target.value }))}
                  placeholder="order_confirmation"
                />
              </div>
              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Order Confirmation"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={createForm.subject_template}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, subject_template: e.target.value }))}
                placeholder="Thanks {{customer_name}}, your order is confirmed"
              />
            </div>
            <div className="space-y-2">
              <Label>Body HTML</Label>
              <Textarea
                value={createForm.body_html_template}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, body_html_template: e.target.value }))}
                className="min-h-[180px]"
                placeholder="<p>Hello {{customer_name}}</p>"
              />
            </div>
            <div className="space-y-2">
              <Label>Dynamic Fields (comma separated)</Label>
              <Input
                value={createForm.dynamic_fields}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, dynamic_fields: e.target.value }))}
                placeholder="customer_name, order_id"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#C97A5E] hover:bg-[#B06850]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
